/**
 * Circular-coax fill model: the five canonical dielectric fills of a circular
 * coaxial line (homogeneous, radial N-layer, azimuthal M-sector wedge,
 * combined shell+wedge, eccentric with optional conformal two-layer), with
 *
 *   - closed-form eps_eff / tand_eff / Z0 / C' (pure TS, instant),
 *   - `.xsec` text generation for the text-expressible fills, and
 *   - typed-Builder authoring for all of them (`authorCoax`), following the
 *     normal/region conventions locked by test/test_geom_cxx.cpp Gate 2b.
 *
 * All dimensions are SI metres. Angles are radians.
 */
import { BoundaryKind } from "./solution.js";
export const ETA0 = 376.730313668;
export const EPS0 = 8.8541878128e-12;
/** Conformal modulus: ln(b/a) concentric, acosh((a^2+b^2-d^2)/2ab) eccentric. */
export function modulus(spec) {
    const { a, b } = spec;
    const d = spec.d ?? 0;
    if (d === 0)
        return Math.log(b / a);
    return Math.acosh((a * a + b * b - d * d) / (2 * a * b));
}
/** Vacuum capacitance C0' = 2*pi*eps0 / M [F/m] — the eps_eff fallback divisor. */
export function vacuumCapacitance(spec) {
    return (2 * Math.PI * EPS0) / modulus(spec);
}
/** Vacuum impedance (eta0/2pi)*M [ohm]. */
export function vacuumImpedance(spec) {
    return (ETA0 / (2 * Math.PI)) * modulus(spec);
}
const TWO_PI = 2 * Math.PI;
function radialWeights(a, layers) {
    let rPrev = a;
    return layers.map((l) => {
        const w = Math.log(l.r / rPrev) / l.er;
        rPrev = l.r;
        return w;
    });
}
/**
 * Closed-form effective permittivity, or null when none exists (a combined
 * fill off the matched-ratio locus e1/e2 == e3/e4).
 */
export function epsEffClosedForm(spec) {
    const { a, b, fill } = spec;
    switch (fill.kind) {
        case "homogeneous":
            return fill.diel.er;
        case "radial": {
            const w = radialWeights(a, fill.layers);
            return Math.log(b / a) / w.reduce((s, x) => s + x, 0);
        }
        case "wedge":
            return fill.sectors.reduce((s, x) => s + x.er * x.phi, 0) / TWO_PI;
        case "combined": {
            const [e1, e2, e3, e4] = fill.eps;
            const rho = e1 / e2;
            if (Math.abs(rho - e3 / e4) > 1e-9 * rho)
                return null;
            const angular = (e1 * fill.phi + e3 * (TWO_PI - fill.phi)) / TWO_PI;
            return (angular * Math.log(b / a)) / (Math.log(fill.rc / a) + rho * Math.log(b / fill.rc));
        }
        case "eccentricRadial":
            return 1 / (fill.t / fill.er1 + (1 - fill.t) / fill.er2);
    }
}
/**
 * Energy-weighted effective loss tangent. Exact for homogeneous / radial /
 * wedge / eccentric-conformal fills and for combined fills on the matched
 * locus; an estimate off it (series-within-sector, parallel across sectors).
 */
export function tandEffClosedForm(spec) {
    const { a, b, fill } = spec;
    switch (fill.kind) {
        case "homogeneous":
            return fill.diel.tand ?? 0;
        case "radial": {
            const w = radialWeights(a, fill.layers);
            const total = w.reduce((s, x) => s + x, 0);
            return fill.layers.reduce((s, l, i) => s + (w[i] / total) * (l.tand ?? 0), 0);
        }
        case "wedge": {
            const num = fill.sectors.reduce((s, x) => s + x.er * x.phi * (x.tand ?? 0), 0);
            const den = fill.sectors.reduce((s, x) => s + x.er * x.phi, 0);
            return den > 0 ? num / den : 0;
        }
        case "combined": {
            const [e1, e2, e3, e4] = fill.eps;
            const [t1, t2, t3, t4] = fill.tands ?? [0, 0, 0, 0];
            const lin = Math.log(fill.rc / a);
            const lout = Math.log(b / fill.rc);
            // Per-sector series stack: eps_k and energy-weighted tand_k.
            const sector = (ein, eout, tin, tout) => {
                const win = lin / ein;
                const wout = lout / eout;
                const eps = Math.log(b / a) / (win + wout);
                const tand = (win * tin + wout * tout) / (win + wout);
                return { eps, tand };
            };
            const s1 = sector(e1, e2, t1, t2);
            const s2 = sector(e3, e4, t3, t4);
            const num = s1.eps * fill.phi * s1.tand + s2.eps * (TWO_PI - fill.phi) * s2.tand;
            const den = s1.eps * fill.phi + s2.eps * (TWO_PI - fill.phi);
            return den > 0 ? num / den : 0;
        }
        case "eccentricRadial": {
            const w1 = fill.t / fill.er1;
            const w2 = (1 - fill.t) / fill.er2;
            return (w1 * (fill.tand1 ?? 0) + w2 * (fill.tand2 ?? 0)) / (w1 + w2);
        }
    }
}
/** Closed-form Z0 [ohm], or null (combined off-locus). */
export function z0ClosedForm(spec) {
    const eps = epsEffClosedForm(spec);
    return eps === null ? null : vacuumImpedance(spec) / Math.sqrt(eps);
}
/** Closed-form C' [F/m], or null (combined off-locus). */
export function capacitanceClosedForm(spec) {
    const eps = epsEffClosedForm(spec);
    return eps === null ? null : eps * vacuumCapacitance(spec);
}
/**
 * The Apollonius interface circle of the bipolar map at conformal fraction t:
 * the closed-form two-layer interface of an eccentric coax (inner conductor
 * at (d,0) radius a, shield at origin radius b). Reduces to a concentric
 * circle as d -> 0. Formulas as locked by test_geom_cxx.cpp Gate 2b.
 */
export function apolloniusCircle(a, b, d, t) {
    if (d === 0) {
        // Concentric limit: ln-space interpolation between a and b.
        return { cx: 0, r: a * Math.pow(b / a, t) };
    }
    const m = (b * b - a * a + d * d) / (2 * d);
    const F = Math.sqrt(m * m - b * b);
    const tauO = Math.atanh(F / (0 - m));
    const tauI = Math.atanh(F / (d - m));
    const tauC = tauI + t * (tauO - tauI);
    return { cx: m + (F * Math.cosh(tauC)) / Math.sinh(tauC), r: Math.abs(F / Math.sinh(tauC)) };
}
const fmt = (x) => {
    const s = String(x);
    // .xsec numbers: plain decimal or scientific, no spaces.
    return s.includes("e") || s.includes("E") ? x.toExponential() : s;
};
/**
 * Emit the `.xsec` text for a text-expressible fill (homogeneous, radial,
 * eccentric two-layer), or null for wedge/combined — a text `conductor` binds
 * one region, but those fills need per-arc regions; use {@link authorCoax}.
 * Dimensions are SI metres (`unit m`).
 */
export function toXsecText(spec) {
    const { a, b, fill } = spec;
    const d = spec.d ?? 0;
    const head = ["version 1", "unit m", `var a = ${fmt(a)}`, `var b = ${fmt(b)}`, `var d = ${fmt(d)}`];
    if (fill.kind === "homogeneous") {
        return [
            ...head,
            `material diel er=${fmt(fill.diel.er)}${fill.diel.tand ? ` tand=${fmt(fill.diel.tand)}` : ""}`,
            "region fill material=diel",
            "conductor signal volt=1 side=inside region=fill",
            "  circle d,0 r=a",
            "end",
            "conductor shield volt=0 side=outside region=fill",
            "  circle 0,0 r=b",
            "end",
            "analysis quasitem",
            "",
        ].join("\n");
    }
    if (fill.kind === "radial") {
        if (d !== 0)
            return null; // concentric only
        const L = fill.layers;
        const lines = [...head];
        L.forEach((l, i) => lines.push(`material m${i} er=${fmt(l.er)}${l.tand ? ` tand=${fmt(l.tand)}` : ""}`));
        let rIn = a;
        L.forEach((l, i) => {
            lines.push(`region L${i} material=m${i} seed=${fmt((rIn + l.r) / 2)},0`);
            rIn = l.r;
        });
        lines.push("conductor signal volt=1 side=inside region=L0", "  circle 0,0 r=a", "end");
        lines.push(`conductor shield volt=0 side=outside region=L${L.length - 1}`, "  circle 0,0 r=b", "end");
        for (let i = 0; i < L.length - 1; i++) {
            lines.push(`interface i${i} region_a=L${i} region_b=L${i + 1} a_inside=true`, `  circle 0,0 r=${fmt(L[i].r)}`, "end");
        }
        lines.push("analysis quasitem", "");
        return lines.join("\n");
    }
    if (fill.kind === "eccentricRadial") {
        const { cx, r: rc } = apolloniusCircle(a, b, d, fill.t);
        // Seeds along +x: the inner conformal layer spans (d+a, cx+rc), the outer
        // (cx+rc, b) — the Apollonius circle encloses the inner conductor.
        return [
            ...head,
            `material m1 er=${fmt(fill.er1)}${fill.tand1 ? ` tand=${fmt(fill.tand1)}` : ""}`,
            `material m2 er=${fmt(fill.er2)}${fill.tand2 ? ` tand=${fmt(fill.tand2)}` : ""}`,
            `region inner material=m1 seed=${fmt((d + a + cx + rc) / 2)},0`,
            `region outer material=m2 seed=${fmt((cx + rc + b) / 2)},0`,
            "conductor signal volt=1 side=inside region=inner",
            "  circle d,0 r=a",
            "end",
            "conductor shield volt=0 side=outside region=outer",
            "  circle 0,0 r=b",
            "end",
            `interface skin region_a=inner region_b=outer a_inside=true`,
            `  circle ${fmt(cx)},0 r=${fmt(rc)}`,
            "end",
            "analysis quasitem",
            "",
        ].join("\n");
    }
    return null; // wedge / combined: not text-expressible (per-arc conductor regions)
}
/**
 * Author `spec` into a typed {@link Builder} (all five fill kinds), REPLACING
 * nothing — call on a fresh builder. Conventions (Gate 2b / xsec_geom.h):
 * region_a sits on the RIGHT of travel; conductor arcs run CCW (increasing
 * theta) on the inner conductor and CW on the shield so the left normal
 * points into the metal; interface normals point from region_a into region_b.
 *
 * `nBase` = segments per full circle (split proportionally per arc, min 8).
 */
export function authorCoax(builder, spec, nBase = 256) {
    const { a, b, fill } = spec;
    const d = spec.d ?? 0;
    const narc = (span) => Math.max(8, Math.round((nBase * span) / TWO_PI));
    const nline = Math.max(8, Math.round(nBase / 8));
    const conductorArc = (region, volt, cx, r, th0, th1) => {
        const id = builder.addBoundary(BoundaryKind.Conductor, volt, region, -1);
        builder.addArc(id, cx, 0, r, th0, th1, narc(Math.abs(th1 - th0)));
    };
    const interfaceArc = (regA, regB, cx, r, th0, th1) => {
        const id = builder.addBoundary(BoundaryKind.Interface, 0, regA, regB);
        builder.addArc(id, cx, 0, r, th0, th1, narc(Math.abs(th1 - th0)));
    };
    const interfaceLine = (regA, regB, x0, y0, x1, y1) => {
        const id = builder.addBoundary(BoundaryKind.Interface, 0, regA, regB);
        builder.addSegment(id, x0, y0, x1, y1, nline);
    };
    switch (fill.kind) {
        case "homogeneous": {
            const reg = builder.addRegion(fill.diel.er);
            conductorArc(reg, 1, d, a, 0, TWO_PI); // CCW: left normal into the metal
            conductorArc(reg, 0, 0, b, TWO_PI, 0); // CW: left normal outward into the shield
            return;
        }
        case "radial": {
            if (d !== 0)
                throw new Error("radial layers require a concentric coax (d = 0)");
            const regs = fill.layers.map((l) => builder.addRegion(l.er));
            conductorArc(regs[0], 1, 0, a, 0, TWO_PI);
            conductorArc(regs[regs.length - 1], 0, 0, b, TWO_PI, 0);
            for (let i = 0; i < fill.layers.length - 1; i++) {
                // CW arc -> outward normal, from region_a (inner layer) into region_b (outer).
                interfaceArc(regs[i], regs[i + 1], 0, fill.layers[i].r, TWO_PI, 0);
            }
            return;
        }
        case "wedge": {
            if (d !== 0)
                throw new Error("wedge sectors require a concentric coax (d = 0)");
            const M = fill.sectors.length;
            const regs = fill.sectors.map((s) => builder.addRegion(s.er));
            let th = 0;
            const bounds = [0];
            for (const s of fill.sectors) {
                th += s.phi;
                bounds.push(th);
            }
            for (let i = 0; i < M; i++) {
                conductorArc(regs[i], 1, 0, a, bounds[i], bounds[i + 1]);
                conductorArc(regs[i], 0, 0, b, bounds[i + 1], bounds[i]);
            }
            // Radial plane at each sector boundary: region_a = the CW-side (previous)
            // sector, region_b = the CCW-side sector the plane opens into.
            for (let i = 0; i < M; i++) {
                const thI = bounds[i];
                const prev = regs[(i - 1 + M) % M];
                interfaceLine(prev, regs[i], a * Math.cos(thI), a * Math.sin(thI), b * Math.cos(thI), b * Math.sin(thI));
            }
            return;
        }
        case "combined": {
            if (d !== 0)
                throw new Error("combined fill requires a concentric coax (d = 0)");
            const { rc, phi } = fill;
            const [e1, e2, e3, e4] = fill.eps;
            const a1 = builder.addRegion(e1); // inner shell, wedge [0,phi)
            const a2 = builder.addRegion(e2); // outer shell, wedge
            const b1 = builder.addRegion(e3); // inner shell, rest [phi,2pi)
            const b2 = builder.addRegion(e4); // outer shell, rest
            const cp = Math.cos(phi);
            const sp = Math.sin(phi);
            conductorArc(a1, 1, 0, a, 0, phi);
            conductorArc(b1, 1, 0, a, phi, TWO_PI);
            conductorArc(a2, 0, 0, b, phi, 0);
            conductorArc(b2, 0, 0, b, TWO_PI, phi);
            // Shell interface arcs (outward normal: CW traversal), inner -> outer.
            interfaceArc(a1, a2, 0, rc, phi, 0);
            interfaceArc(b1, b2, 0, rc, TWO_PI, phi);
            // Radial planes split at rc; region_a = CW-side sector.
            interfaceLine(b1, a1, a, 0, rc, 0);
            interfaceLine(b2, a2, rc, 0, b, 0);
            interfaceLine(a1, b1, a * cp, a * sp, rc * cp, rc * sp);
            interfaceLine(a2, b2, rc * cp, rc * sp, b * cp, b * sp);
            return;
        }
        case "eccentricRadial": {
            const { cx, r: rc } = apolloniusCircle(a, b, d, fill.t);
            const inner = builder.addRegion(fill.er1);
            const outer = builder.addRegion(fill.er2);
            conductorArc(inner, 1, d, a, 0, TWO_PI);
            conductorArc(outer, 0, 0, b, TWO_PI, 0);
            interfaceArc(inner, outer, cx, rc, TWO_PI, 0);
            return;
        }
    }
}
